import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Activity, Loader2, RefreshCw, Download, Search, X, ChevronRight, Wallet, Crown, Clock, TrendingUp, Users, Layers,
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// ADMIN - Activité des utilisateurs, tous modules confondus (lecture seule).
// Pour chaque utilisateur : ce qu'il a utilisé / généré, combien il a dépensé, quand — avec filtres (période,
// modules, recherche, tri). Clic sur une ligne : synthèse par module + historique daté.
// ============================================================

const authFetch = (path) => fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

const fmtAr = (n) => `${Math.round(n || 0).toLocaleString('fr-FR')} Ar`;
const fmtAmount = (n, unit) => (unit === 'cr' ? `${n} crédit${n > 1 ? 's' : ''}` : fmtAr(n));
const fmtDate = (ts) => ts ? new Date(ts * 1000).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDay = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR') : '—';
const ago = (ts) => {
  if (!ts) return '—';
  const s = Date.now() / 1000 - ts;
  if (s < 90) return "à l'instant";
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.round(s / 3600)} h`;
  if (s < 86400 * 30) return `il y a ${Math.round(s / 86400)} j`;
  return fmtDay(new Date(ts * 1000).toISOString());
};
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const PERIODS = [
  { key: 'today', label: "Aujourd'hui", range: () => [ymd(new Date()), ymd(new Date())] },
  { key: '7d', label: '7 jours', range: () => [ymd(new Date(Date.now() - 6 * 86400000)), ymd(new Date())] },
  { key: '30d', label: '30 jours', range: () => [ymd(new Date(Date.now() - 29 * 86400000)), ymd(new Date())] },
  { key: 'month', label: 'Ce mois', range: () => { const n = new Date(); return [ymd(new Date(n.getFullYear(), n.getMonth(), 1)), ymd(n)]; } },
  { key: 'all', label: 'Tout', range: () => ['', ''] },
  { key: 'custom', label: 'Personnalisé', range: null },
];
const SORTS = [
  { key: 'last_activity', label: 'Dernière activité' }, { key: 'spent', label: 'Dépensé' }, { key: 'balance', label: 'Solde restant' }, { key: 'count', label: "Nombre d'actions" },
  { key: 'last_login', label: 'Dernière connexion' }, { key: 'signup', label: "Date d'inscription" }, { key: 'name', label: 'Nom' },
];

const KIND = {
  gen: { label: 'Génération', sign: '−', cls: 'text-rose-300' }, pay: { label: 'Paiement', sign: '−', cls: 'text-rose-300' },
  refund: { label: 'Remboursement', sign: '+', cls: 'text-emerald-300' }, recharge: { label: 'Recharge', sign: '+', cls: 'text-emerald-300' },
  bonus: { label: 'Offert', sign: '+', cls: 'text-sky-300' }, use: { label: 'Utilisation', sign: '', cls: 'text-gray-300' },
};

const Dot = ({ color }) => <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: color }} />;

function Kpi({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] text-gray-400"><Icon className={`w-3.5 h-3.5 ${tone}`} /> {label}</div>
      <div className="text-lg md:text-xl font-bold leading-tight truncate">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 truncate">{sub}</div>}
    </div>
  );
}

// ------------------------------------------------------------ pagination
const PAGE_SIZES = [10, 25, 50, 100];
function Pager({ page, pageCount, pageSize, total, onPage, onSize, compact }) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const nums = [];
  for (let i = 1; i <= pageCount; i++) {
    if (i === 1 || i === pageCount || Math.abs(i - page) <= 1) nums.push(i);
    else if (nums[nums.length - 1] !== '…') nums.push('…');
  }
  const btn = 'px-2.5 py-1.5 rounded-lg text-xs border transition disabled:opacity-30';
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-xs text-gray-400">
      <span>{from}–{to} sur {total}</span>
      <div className="flex items-center gap-1.5">
        <button className={`${btn} bg-white/5 border-white/10 hover:bg-white/10`} disabled={page <= 1} onClick={() => onPage(page - 1)}>‹{compact ? '' : ' Précédent'}</button>
        {nums.map((n, i) => n === '…'
          ? <span key={`e${i}`} className="px-1">…</span>
          : <button key={n} onClick={() => onPage(n)} className={`${btn} ${n === page ? 'bg-sky-500/25 border-sky-400/60 text-white' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>{n}</button>)}
        <button className={`${btn} bg-white/5 border-white/10 hover:bg-white/10`} disabled={page >= pageCount} onClick={() => onPage(page + 1)}>{compact ? '' : 'Suivant '}›</button>
      </div>
      <label className="flex items-center gap-2">Par page
        <select value={pageSize} onChange={e => onSize(Number(e.target.value))} className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1 text-xs text-white">
          {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
    </div>
  );
}

// ------------------------------------------------------------ tiroir de détail
const DRAWER_PERIODS = [
  { key: 'all', label: 'Tout' }, { key: 'today', label: "Aujourd'hui" }, { key: '7d', label: '7 jours' },
  { key: '30d', label: '30 jours' }, { key: 'custom', label: 'Dates…' },
];
const KIND_GROUPS = [
  { key: '', label: 'Tous les types', kinds: null },
  { key: 'spend', label: 'Dépenses', kinds: ['gen', 'pay'] },
  { key: 'use', label: 'Utilisations gratuites', kinds: ['use'] },
  { key: 'refund', label: 'Remboursements', kinds: ['refund'] },
  { key: 'in', label: 'Recharges / offerts', kinds: ['recharge', 'bonus'] },
];
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / 1000;
const dayLabel = (ts) => new Date(ts * 1000).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const hhmm = (ts) => new Date(ts * 1000).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const dayKey = (ts) => ymd(new Date(ts * 1000));

// synthèse par module calculée sur les événements affichés (donc cohérente avec les filtres du tiroir)
function summarize(events, meta) {
  const by = {};
  events.forEach(e => {
    const m = by[e.m] || (by[e.m] = { gens: 0, refunds: 0, uses: 0, spent: 0, recharged: 0, first: 0, last: 0 });
    if (e.k === 'gen') { m.gens += e.n; m.spent += e.a; }
    else if (e.k === 'refund') { m.refunds += e.n; m.spent -= e.a; }
    else if (e.k === 'use') m.uses += e.n;
    else if (e.k === 'pay') m.spent += e.a;
    else if (e.k === 'recharge') m.recharged += e.a;
    if (e.k === 'gen' || e.k === 'use' || e.k === 'pay') {
      m.last = Math.max(m.last, e.t); m.first = m.first ? Math.min(m.first, e.t) : e.t;
    }
  });
  return Object.entries(by).map(([key, m]) => ({
    key, unit: meta[key]?.unit, count: Math.max(0, m.gens - m.refunds) + m.uses, spent: Math.max(0, m.spent),
    recharged: m.recharged, first: m.first, last: m.last,
  })).sort((a, b) => b.count - a.count);
}

function Drawer({ user, modulesMeta, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [mode, setMode] = useState('all');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [search, setSearch] = useState('');
  const [kindKey, setKindKey] = useState('');
  const [modFilter, setModFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    let alive = true;
    setData(null); setErr('');
    authFetch(`/api/admin/activity/users/${user.user_id}`).then(r => r.json())
      .then(d => { if (alive) { if (d.success) setData(d); else setErr(d.message || 'Chargement impossible'); } })
      .catch(() => alive && setErr('Chargement impossible'));
    return () => { alive = false; };
  }, [user.user_id]);

  const meta = useMemo(() => Object.fromEntries(modulesMeta.map(m => [m.key, m])), [modulesMeta]);

  // période du tiroir (heure locale de l'admin)
  const [fromTs, toTs] = useMemo(() => {
    const now = new Date();
    if (mode === 'today') return [startOfDay(now), null];
    if (mode === '7d') return [startOfDay(new Date(now.getTime() - 6 * 86400000)), null];
    if (mode === '30d') return [startOfDay(new Date(now.getTime() - 29 * 86400000)), null];
    if (mode === 'custom') {
      const f = custom.from ? startOfDay(new Date(`${custom.from}T00:00:00`)) : null;
      const t = custom.to ? startOfDay(new Date(`${custom.to}T00:00:00`)) + 86400 : null;
      return [f, t];
    }
    return [null, null];
  }, [mode, custom]);

  // événements après période + type + recherche (sans le filtre module : le tableau « Par module » sert à le choisir)
  const base = useMemo(() => {
    if (!data) return [];
    const kinds = KIND_GROUPS.find(g => g.key === kindKey)?.kinds;
    const needle = search.trim().toLowerCase();
    return [...data.events].sort((a, b) => b.t - a.t).filter(e =>
      (!fromTs || e.t >= fromTs) && (!toTs || e.t < toTs) && (!kinds || kinds.includes(e.k)) &&
      (!needle || `${meta[e.m]?.label || ''} ${e.l || ''} ${KIND[e.k]?.label || ''}`.toLowerCase().includes(needle)));
  }, [data, fromTs, toTs, kindKey, search, meta]);
  const events = useMemo(() => base.filter(e => !modFilter || e.m === modFilter), [base, modFilter]);
  const rows = useMemo(() => summarize(base, meta), [base, meta]);
  const pageCount = Math.max(1, Math.ceil(events.length / pageSize));

  const spentAr = rows.reduce((s, r) => s + (r.unit === 'ar' ? r.spent : 0), 0);
  const rechAr = rows.reduce((s, r) => s + (r.unit === 'ar' ? r.recharged : 0), 0);
  const total = rows.reduce((s, r) => s + r.count, 0);
  const u = data?.user || user;

  // retour page 1 quand la taille de page change ; la page reste toujours valide
  useEffect(() => { setPage(1); }, [pageSize]);
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

  // regroupement par jour
  const days = useMemo(() => {
    const out = [];
    events.slice((page - 1) * pageSize, page * pageSize).forEach(e => {
      const k = dayKey(e.t);
      let g = out[out.length - 1];
      if (!g || g.key !== k) { g = { key: k, ts: e.t, items: [], spent: 0 }; out.push(g); }
      g.items.push(e);
      if (e.unit === 'ar') g.spent += (e.k === 'gen' || e.k === 'pay') ? e.a : (e.k === 'refund' ? -e.a : 0);
    });
    return out;
  }, [events, page, pageSize]);

  const exportHistory = () => {
    const head = ['Date', 'Heure', 'Module', 'Type', 'Détail', 'Montant', 'Unité'];
    const lines = events.map(e => [new Date(e.t * 1000).toLocaleDateString('fr-FR'), hhmm(e.t), meta[e.m]?.label, KIND[e.k]?.label, e.l, e.a || '', e.a ? (e.unit === 'cr' ? 'crédits' : 'Ar') : '']);
    const csv = [head, ...lines].map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = `historique-${(u.email || u.user_id).replace(/[^\w.-]+/g, '_')}-${ymd(new Date())}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-slate-950 border-l border-white/10 flex flex-col">
        {/* ── PARTIE FIXE : identité, filtres, totaux ── */}
        <div className="flex-shrink-0 border-b border-white/10 bg-slate-950">
          <div className="px-5 py-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-bold text-lg truncate">{u.name || u.email}</p>
              <p className="text-xs text-gray-400 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ''}</p>
              <p className="text-xs text-gray-500 mt-1">Inscrit le {fmtDay(u.created_at)} · dernière connexion {ago(u.last_login)} ({u.logins || 0} connexion{(u.logins || 0) > 1 ? 's' : ''})</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition shrink-0"><X className="w-4 h-4" /></button>
          </div>
          {data && (
            <div className="px-5 pb-4 space-y-3">
              <div className="flex flex-wrap items-center gap-1.5">
                {DRAWER_PERIODS.map(p => (
                  <button key={p.key} onClick={() => { setMode(p.key); setPage(1); }}
                    className={`px-3 py-1 rounded-full text-xs border transition ${mode === p.key ? 'bg-sky-500/25 border-sky-400/60' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>{p.label}</button>
                ))}
                {mode === 'custom' && (
                  <span className="flex items-center gap-1.5 text-xs">
                    <input type="date" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1" />
                    <span className="text-gray-500">→</span>
                    <input type="date" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1" />
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[160px]">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Rechercher (module, détail…)"
                    className="w-full bg-black/30 border border-white/10 rounded-lg pl-8 pr-2 py-1.5 text-xs placeholder-gray-500 focus:outline-none focus:border-sky-400" />
                </div>
                <select value={kindKey} onChange={e => { setKindKey(e.target.value); setPage(1); }} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs">
                  {KIND_GROUPS.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2"><div className="text-[11px] text-gray-400">Dépensé</div><div className="font-bold text-sm">{fmtAr(spentAr)}</div></div>
                <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2"><div className="text-[11px] text-gray-400">Actions</div><div className="font-bold text-sm">{total}</div></div>
                <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2"><div className="text-[11px] text-gray-400">Rechargé</div><div className="font-bold text-sm">{fmtAr(rechAr)}</div></div>
              </div>
            </div>
          )}
        </div>

        {/* ── ZONE QUI DÉFILE : soldes, synthèse par module, historique ── */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-6">
          {!data && !err && <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>}
          {err && <p className="text-rose-300 text-sm">{err}</p>}
          {data && (<>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Reste à chaque module <span className="normal-case text-gray-500">(solde actuel)</span></p>
              {data.balances.length === 0 ? <p className="text-sm text-gray-500">Aucun portefeuille ni quota pour cet utilisateur.</p> : (
                <div className="rounded-2xl border border-white/10 overflow-hidden">
                  {data.balances.map(b => (
                    <div key={b.key} className="flex items-center gap-3 px-4 py-2.5 text-sm border-b border-white/5 last:border-0">
                      <Dot color={b.color} />
                      <span className="flex-1 min-w-0"><span className="block truncate">{b.label}</span>{b.sub && <span className="block text-[11px] text-gray-500 truncate" title={b.sub}>{b.sub}</span>}</span>
                      <span className="font-semibold tabular-nums text-emerald-200 text-right">{b.main}</span>
                    </div>
                  ))}
                </div>
              )}
              {data.subscriptions.length > 0 && (
                <div className="flex flex-wrap gap-2 text-xs mt-2">
                  {data.subscriptions.map((sb, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 flex items-center gap-1.5">
                      <Crown className="w-3 h-3" /> {sb.service}{sb.max_videos ? ` · vidéos ${sb.used_videos}/${sb.max_videos}` : ''}{sb.max_images ? ` · images ${sb.used_images}/${sb.max_images}` : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Par module <span className="normal-case text-gray-500">(cliquer pour filtrer l'historique)</span></p>
              {rows.length === 0 ? <p className="text-sm text-gray-500">Aucune activité avec ces filtres.</p> : (
                <div className="rounded-2xl border border-white/10 overflow-hidden">
                  {rows.map(r => (
                    <button key={r.key} onClick={() => { setModFilter(modFilter === r.key ? '' : r.key); setPage(1); }}
                      className={`w-full px-4 py-2.5 text-sm text-left border-b border-white/5 last:border-0 transition ${modFilter === r.key ? 'bg-white/10' : 'hover:bg-white/5'}`}>
                      <span className="flex items-center gap-3">
                        <Dot color={meta[r.key]?.color} />
                        <span className="flex-1 min-w-0 truncate">{meta[r.key]?.label || r.key}</span>
                        <span className="text-gray-300 tabular-nums">{r.count} action{r.count > 1 ? 's' : ''}</span>
                        <span className="w-28 text-right tabular-nums text-gray-200">{r.unit && r.spent ? fmtAmount(r.spent, r.unit) : '—'}</span>
                      </span>
                      {r.last > 0 && (
                        <span className="block ml-5 mt-0.5 text-[11px] text-gray-500">
                          {dayKey(r.first) === dayKey(r.last) ? `Utilisé le ${new Date(r.last * 1000).toLocaleDateString('fr-FR')} à ${hhmm(r.last)}`
                            : `Première utilisation ${fmtDate(r.first)} · dernière ${fmtDate(r.last)}`}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-xs uppercase tracking-wide text-gray-400 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Historique {modFilter ? `· ${meta[modFilter]?.label}` : ''} <span className="normal-case text-gray-500">({events.length})</span></p>
                <span className="flex items-center gap-3">
                  {modFilter && <button onClick={() => setModFilter('')} className="text-xs text-sky-300 hover:underline">Tous les modules</button>}
                  <button onClick={exportHistory} disabled={!events.length} className="text-xs text-sky-300 hover:underline disabled:opacity-40 flex items-center gap-1"><Download className="w-3 h-3" /> CSV</button>
                </span>
              </div>
              {events.length === 0 ? <p className="text-sm text-gray-500">Rien à afficher avec ces filtres.</p> : (
                <div className="space-y-4">
                  {days.map(g => (
                    <div key={g.key}>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="font-semibold text-gray-300 capitalize">{dayLabel(g.ts)}</span>
                        <span className="text-gray-500">{g.items.length} ligne{g.items.length > 1 ? 's' : ''}{g.spent > 0 ? ` · ${fmtAr(g.spent)}` : ''}</span>
                      </div>
                      <div className="space-y-1">
                        {g.items.map((e, i) => {
                          const k = KIND[e.k] || KIND.use;
                          return (
                            <div key={i} className="flex items-center gap-3 rounded-xl bg-white/[0.03] px-3 py-2 text-sm">
                              <span className="text-[11px] text-gray-500 w-10 shrink-0 tabular-nums">{hhmm(e.t)}</span>
                              <Dot color={meta[e.m]?.color} />
                              <span className="flex-1 min-w-0 truncate text-gray-200" title={e.l}>{meta[e.m]?.label}{e.l && e.k === 'use' ? ` · ${e.l}` : (e.k !== 'gen' && e.k !== 'use' ? ` · ${k.label}` : '')}</span>
                              <span className={`tabular-nums shrink-0 ${k.cls}`}>{e.a ? `${k.sign}${fmtAmount(e.a, e.unit)}` : (e.n > 1 ? `×${e.n}` : '')}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {data.events_total > data.events.length && <p className="text-[11px] text-gray-500 text-center">Les {data.events.length} événements les plus récents sont chargés.</p>}
                </div>
              )}
            </div>
          </>)}
        </div>

        {/* ── PAGINATION FIXE de l'historique ── */}
        {data && events.length > 0 && (
          <div className="flex-shrink-0 border-t border-white/10 bg-slate-950 px-5 py-2.5">
            <Pager page={Math.min(page, pageCount)} pageCount={pageCount} pageSize={pageSize} total={events.length} onPage={setPage} onSize={setPageSize} compact />
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ page
export default function UserActivity() {
  const [period, setPeriod] = useState('30d');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [mods, setMods] = useState([]);
  const [q, setQ] = useState('');
  const [qLive, setQLive] = useState('');
  const [sort, setSort] = useState('last_activity');
  const [onlyActive, setOnlyActive] = useState(true);
  const [onlySpenders, setOnlySpenders] = useState(false);
  const [onlyBalance, setOnlyBalance] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState(null);
  const reqId = useRef(0);

  const [from, to] = useMemo(() => {
    if (period === 'custom') return [custom.from, custom.to];
    return PERIODS.find(p => p.key === period).range();
  }, [period, custom]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', from); if (to) p.set('to', to);
    if (mods.length) p.set('modules', mods.join(','));
    return p.toString();
  }, [from, to, mods]);

  const load = useCallback(async (refresh = false) => {
    const id = ++reqId.current;
    setLoading(true); setError('');
    const p = new URLSearchParams(query);
    if (q) p.set('q', q);
    p.set('sort', sort);
    if (onlyActive) p.set('only_active', '1');
    if (onlySpenders) p.set('only_spenders', '1');
    if (onlyBalance) p.set('only_balance', '1');
    if (refresh) p.set('refresh', '1');
    try {
      const r = await authFetch(`/api/admin/activity/users?${p.toString()}`);
      const d = await r.json();
      if (id !== reqId.current) return;           // une requête plus récente a pris le relais
      if (d.success) { setData(d); setPage(1); } else setError(d.message || 'Chargement impossible');
    } catch (_) { if (id === reqId.current) setError('Chargement impossible'); }
    finally { if (id === reqId.current) setLoading(false); }
  }, [query, q, sort, onlyActive, onlySpenders, onlyBalance]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [pageSize]);
  useEffect(() => { const t = setTimeout(() => setQ(qLive.trim()), 350); return () => clearTimeout(t); }, [qLive]);

  const modulesMeta = useMemo(() => data?.modules || [], [data]);
  const balMeta = useMemo(() => Object.fromEntries((data?.bal_meta || []).map(m => [m.key, m])), [data]);
  const meta = useMemo(() => Object.fromEntries(modulesMeta.map(m => [m.key, m])), [modulesMeta]);
  const users = data?.users || [];
  const pageCount = Math.max(1, Math.ceil(users.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const visible = users.slice((safePage - 1) * pageSize, safePage * pageSize);
  const toggleMod = (k) => setMods(m => (m.includes(k) ? m.filter(x => x !== k) : [...m, k]));

  const exportCsv = () => {
    const keys = modulesMeta.map(m => m.key);
    const head = ['Nom', 'Email', 'Téléphone', 'Inscription', 'Dernière activité', 'Dernière connexion', 'Actions', 'Dépensé (Ar)', 'Dépensé (crédits)',
      ...keys.flatMap(k => [`${meta[k].label} - actions`, `${meta[k].label} - dépensé`])];
    const f = (ts) => ts ? new Date(ts * 1000).toLocaleString('fr-FR') : '';
    const rows = users.map(u => [u.name, u.email, u.phone, fmtDay(u.created_at), f(u.last_activity), f(u.last_login), u.count, u.spent_ar, u.spent_cr,
      ...keys.flatMap(k => [u.by_module[k]?.count || 0, u.by_module[k]?.spent || 0])]);
    const csv = [head, ...rows].map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = `activite-utilisateurs-${ymd(new Date())}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const k = data?.kpis;
  const top = k?.top_module ? meta[k.top_module] : null;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] lg:h-screen p-3 md:p-5 gap-3 box-border text-white">
      {/* ── PARTIE FIXE : titre, indicateurs, filtres ── */}
      <div className="flex-shrink-0 space-y-3 w-full max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center"><Activity className="w-5 h-5" /></span>
              Activité des utilisateurs
            </h1>
            <p className="text-xs text-blue-200 mt-0.5">Qui utilise quoi, combien ils dépensent, et quand — tous modules</p>
          </div>
          <div className="flex gap-2">
            <button onClick={exportCsv} disabled={!users.length} className="px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-40 transition text-sm flex items-center gap-2"><Download className="w-4 h-4" /> Export CSV</button>
            <button onClick={() => load(true)} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition" title="Actualiser (recalcule depuis la base)"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
          <Kpi icon={Users} tone="text-sky-300" label="Utilisateurs actifs" value={k ? `${k.users_active}` : '…'} sub={k ? `sur ${k.users_total} inscrits` : ''} />
          <Kpi icon={Wallet} tone="text-emerald-300" label="Total dépensé" value={k ? fmtAr(k.spent_ar) : '…'} sub="portefeuilles + abonnements" />
          <Kpi icon={Wallet} tone="text-teal-300" label="Solde restant" value={k ? fmtAr(k.balance_ar) : '…'} sub="chez les utilisateurs affichés" />
          <Kpi icon={TrendingUp} tone="text-amber-300" label="Actions" value={k ? k.events.toLocaleString('fr-FR') : '…'} sub="générations et utilisations" />
          <Kpi icon={Layers} tone="text-fuchsia-300" label="Module n°1" value={top ? top.label : '—'} sub={top ? `${k.top_module_count} actions` : ''} />
        </div>

        <div className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-2.5">
          <div className="flex flex-wrap items-center gap-1.5">
            {PERIODS.map(p => (
              <button key={p.key} onClick={() => setPeriod(p.key)}
                className={`px-3 py-1 rounded-full text-xs border transition ${period === p.key ? 'bg-sky-500/25 border-sky-400/60' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>{p.label}</button>
            ))}
            {period === 'custom' && (
              <span className="flex items-center gap-1.5 text-xs">
                <input type="date" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1" />
                <span className="text-gray-400">→</span>
                <input type="date" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1" />
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
            <button onClick={() => setMods([])} className={`shrink-0 px-2.5 py-1 rounded-full text-xs border transition ${mods.length === 0 ? 'bg-white/15 border-white/40' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>Tous les modules</button>
            {modulesMeta.map(m => (
              <button key={m.key} onClick={() => toggleMod(m.key)}
                className={`shrink-0 px-2.5 py-1 rounded-full text-xs border transition flex items-center gap-1.5 ${mods.includes(m.key) ? 'bg-white/15 border-white/40' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>
                <Dot color={m.color} /> {m.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={qLive} onChange={e => setQLive(e.target.value)} placeholder="Rechercher un nom, un email, un téléphone…"
                className="w-full bg-black/30 border border-white/10 rounded-xl pl-9 pr-3 py-1.5 text-sm placeholder-gray-500 focus:outline-none focus:border-sky-400" />
            </div>
            <select value={sort} onChange={e => setSort(e.target.value)} className="bg-black/40 border border-white/10 rounded-xl px-3 py-1.5 text-sm">
              {SORTS.map(sx => <option key={sx.key} value={sx.key}>Trier : {sx.label}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"><input type="checkbox" checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)} className="accent-sky-400" /> Actifs seulement</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"><input type="checkbox" checked={onlySpenders} onChange={e => setOnlySpenders(e.target.checked)} className="accent-sky-400" /> Qui ont dépensé</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"><input type="checkbox" checked={onlyBalance} onChange={e => setOnlyBalance(e.target.checked)} className="accent-sky-400" /> Avec un solde</label>
          </div>
        </div>
        {error && <p className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-2">{error}</p>}
      </div>

      {/* ── LISTE QUI DÉFILE (en-tête de colonnes figé) ── */}
      <div className="flex-1 min-h-0 w-full max-w-7xl mx-auto overflow-auto rounded-2xl border border-white/10 bg-white/[0.02]">
        <div className="hidden md:grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_110px_120px_70px_110px_24px] gap-3 px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-400 border-b border-white/10 bg-slate-900 sticky top-0 z-10">
          <span>Utilisateur</span><span>Activité par module</span><span className="text-right">Dépensé</span><span className="text-right">Solde restant</span><span className="text-right">Actions</span><span className="text-right">Dernière activité</span><span />
        </div>
        {loading && !data ? (
          <div className="py-16 flex flex-col items-center gap-2 text-sm text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /> Calcul en cours…</div>
        ) : users.length === 0 ? (
          <p className="py-14 text-center text-sm text-gray-400">Aucun utilisateur ne correspond à ces filtres.</p>
        ) : visible.map(u => {
          const chips = Object.entries(u.by_module).filter(([, v]) => v.count > 0 || v.spent > 0).sort((a, b) => b[1].count - a[1].count);
          return (
            <button key={u.user_id} onClick={() => setSelected(u)}
              className={`w-full text-left grid md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_110px_120px_70px_110px_24px] gap-x-3 gap-y-2 px-4 py-3 items-center border-b border-white/5 hover:bg-white/5 transition ${loading ? 'opacity-60' : ''}`}>
              <span className="min-w-0 flex items-center gap-3">
                <span className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-sky-500/40 to-indigo-500/40 flex items-center justify-center font-bold text-sm">{(u.name || u.email || '?').charAt(0).toUpperCase()}</span>
                <span className="min-w-0"><span className="block font-semibold truncate">{u.name || u.email}</span><span className="block text-xs text-gray-400 truncate">{u.name ? u.email : ''}{u.name ? ' · ' : ''}inscrit le {fmtDay(u.created_at)}</span></span>
              </span>
              <span className="flex flex-wrap gap-1.5 min-w-0">
                {chips.length === 0 && <span className="text-xs text-gray-500">Aucune activité</span>}
                {chips.slice(0, 3).map(([key, v]) => (
                  <span key={key} className="px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-[11px] flex items-center gap-1.5">
                    <Dot color={meta[key]?.color} />{meta[key]?.label.replace('Utile · ', '').replace(' Studio', '')} <b>{v.count}</b>{v.spent && meta[key]?.unit === 'ar' ? <span className="text-gray-400">· {fmtAr(v.spent)}</span> : null}
                  </span>
                ))}
                {chips.length > 3 && <span className="px-2 py-0.5 rounded-md bg-white/5 text-[11px] text-gray-400">+{chips.length - 3}</span>}
              </span>
              <span className="md:text-right font-semibold tabular-nums">{u.spent_ar ? fmtAr(u.spent_ar) : <span className="text-gray-500 font-normal">—</span>}{u.spent_cr ? <span className="block text-[11px] text-gray-400 font-normal">{u.spent_cr} cr.</span> : null}</span>
              <span className="md:text-right tabular-nums" title={Object.entries(u.balances || {}).map(([key, b]) => `${balMeta[key]?.label || key} : ${b.main}`).join('\n')}>
                <span className="md:hidden text-gray-500 text-xs">Solde : </span>
                {u.balance_ar ? <span className="font-semibold text-emerald-300">{fmtAr(u.balance_ar)}</span> : <span className="text-gray-500">—</span>}
                {u.balance_cr ? <span className="block text-[11px] text-gray-400">+ {u.balance_cr} cr. CV</span> : null}
              </span>
              <span className="md:text-right tabular-nums text-gray-300"><span className="md:hidden text-gray-500 text-xs">Actions : </span>{u.count}</span>
              <span className="md:text-right text-xs text-gray-400">{ago(u.last_activity)}</span>
              <ChevronRight className="w-4 h-4 text-gray-500 hidden md:block" />
            </button>
          );
        })}
        {users.length > 0 && (
          <p className="text-[11px] text-gray-500 px-4 py-4">
            « Dépensé » = générations payées sur les portefeuilles (remboursements déduits) + abonnements. Les crédits CV sont comptés à part. Voice Kar et les outils gratuits d'Utile sont historisés depuis le 16/10/2026 (les utilisations du 15/10 ont été reconstituées depuis les journaux du serveur) ;
            les modules à quotas (Pub Vidéos…) n'affichent que leur consommation cumulée dans la fiche détaillée. « Solde restant » = ce qu'il reste maintenant sur les portefeuilles (Vidéo, Image, Chansons, CM Studio) ; le détail de chaque module, y compris les crédits CV et les quotas, est dans la fiche.
          </p>
        )}
      </div>

      {/* ── PAGINATION FIXE ── */}
      <div className="flex-shrink-0 w-full max-w-7xl mx-auto">
        <Pager page={safePage} pageCount={pageCount} pageSize={pageSize} total={users.length} onPage={setPage} onSize={setPageSize} />
        {data && <p className="text-[11px] text-gray-500 mt-1">Données du {new Date(data.built_at * 1000).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} · actualisées toutes les 2 minutes</p>}
      </div>

      {selected && <Drawer user={selected} modulesMeta={modulesMeta} onClose={() => setSelected(null)} />}
    </div>
  );
}
